import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../utils/apiUtils';

const WorkPlanForm = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    title: '',
    total_duration: '',
    planned_start: '',
    planned_end: ''
  });

  const [planningRows, setPlanningRows] = useState([
    {
      id: 1,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    },
    {
      id: 2,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    },
    {
      id: 3,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    },
    {
      id: 4,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    },
    {
      id: 5,
      objectives: '',
      expected_output: '',
      activities: '',
      months: {
        M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
        M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
      }
    }
  ]);

  // Set up axios default headers
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlanningRowChange = (rowId, field, value) => {
    setPlanningRows(prev => 
      prev.map(row => 
        row.id === rowId 
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const handleMonthToggle = (rowId, month) => {
    setPlanningRows(prev => 
      prev.map(row => 
        row.id === rowId 
          ? { 
              ...row, 
              months: { 
                ...row.months, 
                [month]: !row.months[month] 
              } 
            }
          : row
      )
    );
  };

  const addPlanningRow = () => {
    const newId = Math.max(...planningRows.map(row => row.id)) + 1;
    setPlanningRows(prev => [
      ...prev,
      {
        id: newId,
        objectives: '',
        expected_output: '',
        activities: '',
        months: {
          M1: '', M2: '', M3: '', M4: '', M5: '', M6: '',
          M7: '', M8: '', M9: '', M10: '', M11: '', M12: ''
        }
      }
    ]);
  };

  const removePlanningRow = (rowId) => {
    if (planningRows.length > 1) {
      setPlanningRows(prev => prev.filter(row => row.id !== rowId));
    }
  };

  useEffect(() => {
    const loadForEdit = async () => {
      if (!id) return;
      setLoading(true);
      setError('');
      try {
      const { data } = await apiClient.get(`${API_ENDPOINTS.WORK_PLANS}/${id}`);
        setForm({
          title: data.title || '',
          total_duration: data.total_duration || '',
          planned_start: data.planned_start ? data.planned_start.substring(0,10) : '',
          planned_end: data.planned_end ? data.planned_end.substring(0,10) : ''
        });
        if (Array.isArray(data.planning_rows) && data.planning_rows.length) {
          const rows = data.planning_rows.map((r, idx) => ({
            id: idx + 1,
            objectives: r.objectives || '',
            expected_output: r.expected_output || '',
            activities: r.activities || '',
            months: r.months || { M1:false,M2:false,M3:false,M4:false,M5:false,M6:false,M7:false,M8:false,M9:false,M10:false,M11:false,M12:false }
          }));
          
          // Ensure at least 5 default rows
          if (rows.length < 5) {
            const defaultRows = Array.from({ length: 5 - rows.length }, (_, i) => ({
              id: rows.length + i + 1,
              objectives: '',
              expected_output: '',
              activities: '',
              months: {
                M1: false, M2: false, M3: false, M4: false, M5: false, M6: false,
                M7: false, M8: false, M9: false, M10: false, M11: false, M12: false
              }
            }));
            setPlanningRows([...rows, ...defaultRows]);
          } else {
            setPlanningRows(rows);
          }
        }
      } catch (err) {
        console.error('Failed to load work plan', err);
        setError('Failed to load work plan.');
      } finally {
        setLoading(false);
      }
    };
    loadForEdit();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate date range if both dates are provided
      if (form.planned_start && form.planned_end) {
        const startDate = new Date(form.planned_start);
        const endDate = new Date(form.planned_end);
        
        if (endDate <= startDate) {
          setError('Planned End date must be after Planned Start date');
          setLoading(false);
          return;
        }
      }

      // Prepare data for API call
      const workPlanData = {
        ...form,
        planning_table: planningRows
      };
      
      if (id) {
      await apiClient.put(`${API_ENDPOINTS.WORK_PLANS}/${id}`, workPlanData);
        setSuccess('Work plan updated successfully!');
      } else {
      await apiClient.post(API_ENDPOINTS.WORK_PLANS, workPlanData);
        setSuccess('Work plan created successfully!');
      }

      setTimeout(() => {
        navigate('/plan');
      }, 1200);

    } catch (err) {
      console.error('Error creating work plan:', err);
      setError('Failed to create work plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="work-plan-form-container">
      <Container fluid className="py-4">
        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-1">
                  <i className="bi bi-calendar-check me-2"></i>
                  {id ? 'Edit Work Plan' : 'Create Work Plan'}
                </h2>
                <p className="text-muted mb-0">
                  {id ? 'Update this work plan\'s details' : 'Create a new work plan for your extension activities'}
                </p>
              </div>
              <Button variant="outline-secondary" as={Link} to="/plan">
                <i className="bi bi-arrow-left me-2"></i>
                Back to Work Plans
              </Button>
            </div>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                <i className="bi bi-check-circle me-2"></i>
                {success}
              </Alert>
            )}

            <Card className="shadow-sm">
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  {/* Title */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Title
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      placeholder="Enter work plan title"
                      maxLength={500}
                    />
                  </Form.Group>

                  {/* Total Duration */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Total Duration (in months)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="total_duration"
                      value={form.total_duration}
                      onChange={handleChange}
                      placeholder="e.g., 6 months, 1 year, 18 months"
                      maxLength={500}
                    />
                  </Form.Group>

                  {/* Planned Start and End Dates */}
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Planned Start
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="planned_start"
                          value={form.planned_start}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Planned End
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="planned_end"
                          value={form.planned_end}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Planning Table */}
                  <div className="mb-4">
                    <h5 className="mb-3">Work Plan Details</h5>
                    
                    <div className="table-responsive" style={{ overflowX: 'auto' }}>
                      <table className="table table-bordered" style={{ minWidth: '1400px' }}>
                        <thead className="table-light">
                          <tr>
                            <th className="text-center" style={{ width: '250px', minWidth: '250px' }}>Objectives</th>
                            <th className="text-center" style={{ width: '250px', minWidth: '250px' }}>Expected Output</th>
                            <th className="text-center" style={{ width: '400px', minWidth: '400px' }}>Activities</th>
                            <th colSpan="12" className="text-center">Y1</th>
                            <th className="text-center" style={{ width: '80px', minWidth: '80px' }}>Actions</th>
                          </tr>
                          <tr>
                            <th></th>
                            <th></th>
                            <th></th>
                            {['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'].map(month => (
                              <th key={month} className="text-center small" style={{ width: '0.1%' }}>{month}</th>
                            ))}
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {planningRows.map((row, index) => (
                            <tr key={row.id}>
                              <td style={{ width: '250px', minWidth: '250px' }}>
                                <Form.Control
                                  as="textarea"
                                  rows="4"
                                  value={row.objectives}
                                  onChange={(e) => handlePlanningRowChange(row.id, 'objectives', e.target.value)}
                                  placeholder="Enter objectives..."
                                  style={{ border: 'none', resize: 'none', width: '100%' }}
                                />
                              </td>
                              <td style={{ width: '250px', minWidth: '250px' }}>
                                <Form.Control
                                  as="textarea"
                                  rows="4"
                                  value={row.expected_output}
                                  onChange={(e) => handlePlanningRowChange(row.id, 'expected_output', e.target.value)}
                                  placeholder="Enter expected output..."
                                  style={{ border: 'none', resize: 'none', width: '100%' }}
                                />
                              </td>
                              <td style={{ width: '400px', minWidth: '400px' }}>
                                <Form.Control
                                  as="textarea"
                                  rows="4"
                                  value={row.activities}
                                  onChange={(e) => handlePlanningRowChange(row.id, 'activities', e.target.value)}
                                  placeholder="Enter activities..."
                                  style={{ border: 'none', resize: 'none', width: '100%' }}
                                />
                              </td>
                              {['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'].map(month => (
                                <td 
                                  key={month} 
                                  className="text-center"
                                  onClick={() => handleMonthToggle(row.id, month)}
                                  style={{ 
                                    cursor: 'pointer',
                                    backgroundColor: row.months[month] ? '#dc3545' : 'transparent',
                                    border: '1px solid #dee2e6',
                                    minWidth: '40px',
                                    height: '40px'
                                  }}
                                  title={row.months[month] ? 'Click to deselect' : 'Click to select'}
                                >
                                </td>
                              ))}
                              <td className="text-center" style={{ width: '80px', minWidth: '80px' }}>
                                {row.id > 5 && (
                                  <Button
                                    type="button"
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removePlanningRow(row.id)}
                                    title="Delete this row"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="d-flex justify-content-start mt-2">
                      <Button 
                        type="button" 
                        variant="outline-primary" 
                        size="sm"
                        onClick={addPlanningRow}
                      >
                        <i className="bi bi-plus-lg me-1"></i>
                        Add Row
                      </Button>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="d-flex justify-content-end mt-4">
                    <div className="d-flex gap-2">
                      <Button 
                        variant="outline-secondary" 
                        as={Link} 
                        to="/plan"
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        variant="success" 
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-lg me-2"></i>
                            {id ? 'Save Changes' : 'Submit Work Plan'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default WorkPlanForm;
